import { Vector3 } from 'three/webgpu';
import type { HollowDef } from '../universe/HollowDef';
import { perpendicularBasis, type TunnelMouth, wallCrossing } from '../universe/Tunnel';
import type { PhysicsWorld, StaticMesh } from './PhysicsWorld';

/**
 * Collision for a smooth hollow wall (the sandbox; terrain replaces this in M2).
 * A patch of the sphere around the player is rebuilt as they move. Around each
 * tunnel mouth, an exact "collar" follows the cylinder/sphere intersection so
 * there is no gap between the jagged patch hole and the tunnel.
 */
const PATCH_HALF = 70;
const REBUILD_DISTANCE = 22;
const NEAR_WALL = 90;
const HOLE_MARGIN = 2.5;
const COLLAR_RINGS = [0, 0.75, 1.5, 2.5, 3.5, 5];
const COLLAR_SEGMENTS = 96;

const _w = new Vector3();
const _q = new Vector3();

export class HollowWallCollider {
  private patch: StaticMesh | null = null;
  private readonly patchDir = new Vector3();
  private readonly collars: StaticMesh[] = [];

  constructor(
    private readonly physics: PhysicsWorld,
    readonly hollow: HollowDef,
    private readonly mouths: TunnelMouth[],
  ) {
    for (const m of mouths) this.collars.push(this.buildCollar(m));
  }

  update(player: Vector3): void {
    const d = player.distanceTo(this.hollow.center);
    if (Math.abs(d - this.hollow.radius) > NEAR_WALL) {
      if (this.patch) {
        this.physics.removeStaticMesh(this.patch);
        this.patch = null;
      }
      return;
    }
    _w.subVectors(player, this.hollow.center).divideScalar(d);
    if (this.patch && _w.angleTo(this.patchDir) * this.hollow.radius < REBUILD_DISTANCE) return;
    this.rebuild(_w);
  }

  private nearMouth(p: Vector3, extra: number): boolean {
    for (const m of this.mouths) {
      _q.subVectors(p, m.point);
      const t = _q.dot(m.dir);
      if (Math.abs(t) > 25) continue;
      _q.addScaledVector(m.dir, -t);
      if (_q.length() < m.radius + extra) return true;
    }
    return false;
  }

  private rebuild(dir: Vector3): void {
    const R = this.hollow.radius;
    const c = this.hollow.center;
    this.patchDir.copy(dir);
    const anchor = c.clone().addScaledVector(dir, R);
    const nearAnyMouth = this.nearMouth(anchor, PATCH_HALF + 20);
    const step = nearAnyMouth ? 0.75 : 1.5;
    const n = Math.ceil((2 * PATCH_HALF) / step);
    const e1 = new Vector3();
    const e2 = new Vector3();
    perpendicularBasis(dir, e1, e2);
    // e1 × e2 = dir (outward); swap so triangle normals face the hollow centre.
    const tmp = e1.clone();
    e1.copy(e2);
    e2.copy(tmp);
    const verts = new Float32Array((n + 1) * (n + 1) * 3);
    const blocked = new Uint8Array((n + 1) * (n + 1));
    const p = new Vector3();
    const pu = new Vector3();
    for (let i = 0; i <= n; i++) {
      for (let j = 0; j <= n; j++) {
        const u = -PATCH_HALF + i * step;
        const v = -PATCH_HALF + j * step;
        p.copy(dir)
          .multiplyScalar(R)
          .addScaledVector(e1, u)
          .addScaledVector(e2, v)
          .normalize()
          .multiplyScalar(R);
        const k = i * (n + 1) + j;
        verts[k * 3] = p.x - dir.x * R;
        verts[k * 3 + 1] = p.y - dir.y * R;
        verts[k * 3 + 2] = p.z - dir.z * R;
        pu.copy(p).add(c);
        blocked[k] = this.nearMouth(pu, HOLE_MARGIN) ? 1 : 0;
      }
    }
    const idx: number[] = [];
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        const a = i * (n + 1) + j;
        const b = (i + 1) * (n + 1) + j;
        const cc = i * (n + 1) + j + 1;
        const d = (i + 1) * (n + 1) + j + 1;
        if (blocked[a] || blocked[b] || blocked[cc] || blocked[d]) continue;
        idx.push(a, b, cc, b, d, cc);
      }
    }
    if (this.patch) this.physics.removeStaticMesh(this.patch);
    this.patch = this.physics.addStaticMesh(anchor, verts, new Uint32Array(idx));
    this.physics.wakeAll();
  }

  private buildCollar(m: TunnelMouth): StaticMesh {
    const N = new Vector3();
    const B = new Vector3();
    perpendicularBasis(m.dir, N, B);
    const rings = COLLAR_RINGS.length;
    const cols = COLLAR_SEGMENTS;
    const verts = new Float32Array(rings * cols * 3);
    const off = new Vector3();
    for (let k = 0; k < rings; k++) {
      const rho = m.radius + COLLAR_RINGS[k];
      for (let j = 0; j < cols; j++) {
        const phi = (j / cols) * Math.PI * 2;
        off
          .copy(N)
          .multiplyScalar(rho * Math.cos(phi))
          .addScaledVector(B, rho * Math.sin(phi));
        let t = wallCrossing(m, off, m.hollow.radius);
        if (!Number.isFinite(t)) t = 0;
        const i = (k * cols + j) * 3;
        verts[i] = off.x + m.dir.x * t;
        verts[i + 1] = off.y + m.dir.y * t;
        verts[i + 2] = off.z + m.dir.z * t;
      }
    }
    const idx: number[] = [];
    for (let k = 0; k < rings - 1; k++) {
      for (let j = 0; j < cols; j++) {
        const a = k * cols + j;
        const b = (k + 1) * cols + j;
        const c = k * cols + ((j + 1) % cols);
        const d = (k + 1) * cols + ((j + 1) % cols);
        idx.push(a, b, c, b, d, c);
      }
    }
    return this.physics.addStaticMesh(m.point, verts, new Uint32Array(idx));
  }

  dispose(): void {
    if (this.patch) this.physics.removeStaticMesh(this.patch);
    for (const c of this.collars) this.physics.removeStaticMesh(c);
    this.patch = null;
    this.collars.length = 0;
  }
}
